import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import { Button, ConfigProvider, Divider, Flex, Layout, Typography, Input, Dropdown } from 'antd';

const { Search } = Input;
import { debounce } from 'next/dist/server/utils';
import { UrlObject } from 'url';
import CatalogHeaderMenu from '@/components/Catalog/CatalogHeaderMenu/CatalogHeaderMenu';
import { theme } from 'antd';

import h from './Header.module.scss';

import image_logo_full from '/public/static/images/image_logo_full.svg';
import icon_basket from '/public/static/images/icon_basket.svg';
import icon_user from '/public/static/images/icon_user.svg';
import icon_compare from '/public/static/images/icon_compare_default.svg';
import icon_star from '/public/static/images/icon_star.svg';
import icon_signout from '/public/static/images/icon_signout.svg';
import icon_search from '/public/static/images/icon_search.svg';
import HeaderSearchItem from '@/components/HeaderSearchItem/HeaderSearchItem';
import {
    EHeaderSearchItemType,
    ELocalization,
    EProfileType,
    NLocalization,
    NProfileType,
} from 'tkmp-lib/src/enums';
import { AuthService } from 'tkmp-lib/src/services/AuthService';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/utils/hooks/useStores';
import { usePopup } from 'tkmp-lib/src/hooks/usePopup';
import {
    CloseOutlined,
    FactoryIcon,
    HelpIcon,
    LoginIcon,
    PhoneIcon,
    ProductOutlined,
    UserIcon,
} from 'tkmp-lib/src/icons/icons';
import { CustomTransparentButton, CustomTransparentLink } from '@/widgets/ItemButtons/ItemButtons';
import ib from '@/widgets/ItemButtons/ItemButtons.module.scss';
import useLocalization from 'tkmp-lib/src/hooks/useLocalization';
import { IconFlagCN, IconFlagEN, IconFlagRU } from 'tkmp-lib/src/icons/IconsCountries';
import { usePathname, useRouter as useRefreshRouter } from 'next/navigation';

import icon_flag_ru from '/public/static/images/icon_flag_ru.svg';
import icon_flag_en from '/public/static/images/icon_flag_en.svg';
import icon_flag_cn from '/public/static/images/icon_flag_cn.svg';
import { QueryService } from 'tkmp-lib/src/services/QueryService';

type TCredentials = {
    fio: string;
    email: string;
    job: string;
};

const Header = observer(() => {
    const pathname = usePathname();
    const LOCALIZATION = useLocalization();

    const [isShowCatalogHeaderMenu, setIsShowCatalogHeaderMenu] = useState(false);
    const [searchPrompt, setSearchPrompt] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [searchResult, setSearchResult] = useState<any>(null);
    const [isShowSearchResult, setIsShowSearchResult] = useState(false);
    const searchRef = useRef(null);
    const resultRef = useRef(null);
    const router = useRouter();
    const refreshRouter = useRefreshRouter();
    const { message } = usePopup();

    useEffect(() => {
        setSearchPrompt('');
    }, [router.pathname]);

    const {
        modalsStore: { openLoginForm, openRegisterForm, openLocationForm },
        userStore: { USERNAME, USERROLE, clearUser },
    } = useStores();

    const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
    const [credentialsDto, setCredentialsDto] = useState<TCredentials>({
        fio: LOCALIZATION.header.notSpecified,
        email: LOCALIZATION.header.notSpecified,
        job: LOCALIZATION.header.notSpecified,
    });

    useEffect(() => {
        console.log(USERNAME);
        AuthService.checkRefresh().then((answer: any) => {
            console.log(answer);
            setIsAuthorized(answer);
        });
    }, [USERNAME]);

    useEffect(() => {
        if (isAuthorized) {
            AuthService.getCredentials(LOCALIZATION.locale).then((answer) => {
                setCredentialsDto({
                    fio: answer?.fio ?? LOCALIZATION.header.notSpecified,
                    email: answer?.email ?? LOCALIZATION.header.notSpecified,
                    job: answer?.job ?? LOCALIZATION.header.notSpecified,
                });
            });
        }
    }, [isAuthorized]);

    const contextMenuLanguage = [
        {
            key: '0',
            label: (
                <Link
                    as={pathname}
                    href={pathname}
                    locale={'ru'}
                    style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                    <Image
                        src={icon_flag_ru}
                        width={24}
                        height={24}
                        sizes={'100%'}
                        alt={''}
                    />
                    <span>Русский</span>
                </Link>
            ),
        },
        {
            key: '1',
            label: (
                <Link
                    as={pathname}
                    href={pathname}
                    locale={'en'}
                    style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                    <Image
                        src={icon_flag_en}
                        width={24}
                        height={24}
                        sizes={'100%'}
                        alt={''}
                    />
                    <span>English</span>
                </Link>
            ),
        },
        {
            key: '2',
            label: (
                <Link
                    as={pathname}
                    href={pathname}
                    locale={'cn'}
                    style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                >
                    <Image
                        src={icon_flag_cn}
                        width={24}
                        height={24}
                        sizes={'100%'}
                        alt={''}
                    />
                    <span>中文</span>
                </Link>
            ),
        },
    ];
    // @ts-ignore
    const contextMenuUser = [
        {
            key: '0',
            label: (
                <Link
                    href={`/profile`}
                    onClick={() => {
                        setIsShowCatalogHeaderMenu(false);
                    }}
                    className={h.contextMenu_button}
                >
                    <Flex className={h.contextMenu_button_block}>
                        <Image
                            width={24}
                            height={24}
                            src={icon_user}
                            alt={''}
                        />
                        <Typography.Text>
                            {LOCALIZATION.widgets.Header.contextMenuUser.myProfile}
                        </Typography.Text>
                    </Flex>
                </Link>
            ),
            access: [EProfileType.Buyer, EProfileType.Manufacturer, EProfileType.Distributer],
        },
        {
            key: '1',
            label: (
                <Button
                    type={'link'}
                    onClick={() => {
                        setIsShowCatalogHeaderMenu(false);
                        if (process.env.NEXT_PUBLIC_DEBUG !== 'true') {
                            window.location.href = `${process.env.NEXT_PUBLIC_DOMAIN_ADMIN}`;
                        } else {
                            router.push(`${process.env.NEXT_PUBLIC_DOMAIN_ADMIN}`);
                        }
                    }}
                    className={h.contextMenu_button}
                >
                    <Flex className={h.contextMenu_button_block}>
                        <Image
                            width={24}
                            height={24}
                            src={icon_user}
                            alt={''}
                        />
                        <Typography.Text>
                            {LOCALIZATION.widgets.Header.contextMenuUser.adminPanel}
                        </Typography.Text>
                    </Flex>
                </Button>
            ),
            access: [EProfileType.Admin],
        },
        {
            key: '2',
            label: (
                <Link
                    href={`/profile/basket`}
                    onClick={() => {
                        setIsShowCatalogHeaderMenu(false);
                    }}
                    className={h.contextMenu_button}
                >
                    <Flex className={h.contextMenu_button_block}>
                        <Image
                            width={24}
                            height={24}
                            src={icon_basket}
                            alt={''}
                        />
                        <Typography.Text>
                            {LOCALIZATION.widgets.Header.contextMenuUser.basket}
                        </Typography.Text>
                    </Flex>
                </Link>
            ),
            access: [EProfileType.Buyer],
        },
        {
            key: '3',
            label: (
                <Link
                    href={`/profile/compare`}
                    onClick={() => {
                        setIsShowCatalogHeaderMenu(false);
                    }}
                    className={h.contextMenu_button}
                >
                    <Flex className={h.contextMenu_button_block}>
                        <Image
                            style={{ filter: 'invert(1)' }}
                            width={24}
                            height={24}
                            src={icon_compare}
                            alt={''}
                        />
                        <Typography.Text>
                            {LOCALIZATION.widgets.Header.contextMenuUser.compare}
                        </Typography.Text>
                    </Flex>
                </Link>
            ),
            access: [EProfileType.Buyer],
        },
        {
            key: '4',
            label: (
                <Link
                    href={`/profile/favourites`}
                    onClick={() => {
                        setIsShowCatalogHeaderMenu(false);
                    }}
                    className={h.contextMenu_button}
                >
                    <Flex className={h.contextMenu_button_block}>
                        <Image
                            style={{ filter: 'invert(1)' }}
                            width={24}
                            height={24}
                            src={icon_star}
                            alt={''}
                        />
                        <Typography.Text>
                            {LOCALIZATION.widgets.Header.contextMenuUser.favourite}
                        </Typography.Text>
                    </Flex>
                </Link>
            ),
            access: [EProfileType.Buyer],
        },
        {
            key: '5',
            label: (
                <Flex
                    className={h.contextMenu_button}
                    onClick={() => {
                        AuthService.logout().then((answer) => {
                            if (answer) {
                                clearUser();
                                router.push('/');
                            }
                        });
                    }}
                >
                    <Flex className={h.contextMenu_button_block}>
                        <Image
                            width={24}
                            height={24}
                            src={icon_signout}
                            alt={'signOut'}
                        />
                        <Typography.Text>
                            {LOCALIZATION.widgets.Header.contextMenuUser.exit}
                        </Typography.Text>
                    </Flex>
                </Flex>
            ),
            access: [
                EProfileType.Buyer,
                EProfileType.Admin,
                EProfileType.Manufacturer,
                EProfileType.Distributer,
                EProfileType.Unknown,
            ],
        },
    ].filter((f: { key: string; label: any; access: any }) => f.access.includes(USERROLE));

    useEffect(() => {
        if (searchPrompt !== '') {
            const url = `${
                process.env.NEXT_PUBLIC_DOMAIN_BFF
            }/tkmp/v1/ru/search/global?alias=${encodeURIComponent(searchPrompt)}`;

            QueryService.request(url).then((answer: any) => {
                if (process.env.NEXT_PUBLIC_DEBUG === 'true') console.log('SEARCH ->', answer);
                setSearchResult(answer);
            });
        }
    }, [searchPrompt]);

    const handleSearch = (value: React.SetStateAction<string>) => {
        setSearchPrompt(value);
        setIsShowSearchResult(true);
    };

    const handleSearch_realTime = useCallback(debounce(handleSearch, 500), []);

    // @ts-ignore
    return (
        <Flex className={h.container}>
            {process.env.NEXT_PUBLIC_DEBUG === 'true' && (
                <span
                    style={{
                        position: 'absolute',
                        top: '-4px',
                        left: '4px',
                        color: 'green',
                        zIndex: '9999',
                        fontFamily: 'monospace',
                    }}
                >
                    DEBUG
                </span>
            )}
            <Flex
                vertical
                className={h.container_wrapper}
            >
                <Flex
                    vertical
                    className={h.container_body}
                >
                    <ConfigProvider
                        theme={{
                            algorithm: theme.darkAlgorithm,
                            components: {
                                Input: {
                                    colorBgContainer: 'transparent',
                                    colorBorder: '#DCFF00',
                                    colorPrimaryHover: '#DCFF00',
                                    colorText: '#fff',
                                    borderRadius: 8,
                                    fontSize: 16,
                                    paddingBlock: 6,
                                },
                                Dropdown: {
                                    colorBgElevated: '#0B0B0C',
                                    colorText: '#fff',
                                },
                            },
                        }}
                    >
                        <Flex
                            className={h.header_top}
                            align={'center'}
                            justify={'space-between'}
                        >
                            <Flex className={h.header_top__left}>
                                <Link
                                    className={h.header_top__left__imageLink}
                                    href={'/'}
                                    onClick={() => {
                                        setIsShowCatalogHeaderMenu(false);
                                    }}
                                >
                                    <Image
                                        className={h.header_top__left__Image}
                                        priority={true}
                                        src={image_logo_full}
                                        height={0}
                                        width={0}
                                        sizes={'100%'}
                                        alt="TKMP.Istok"
                                    />
                                </Link>
                                <Button
                                    type="primary"
                                    icon={
                                        !isShowCatalogHeaderMenu ? (
                                            <ProductOutlined />
                                        ) : (
                                            <CloseOutlined />
                                        )
                                    }
                                    className={`text-16-400 ${h.header_button_catalog}`}
                                    onClick={() => {
                                        setIsShowCatalogHeaderMenu(!isShowCatalogHeaderMenu);
                                    }}
                                >
                                    {LOCALIZATION.header.catalogButton}
                                </Button>
                                <Flex className={h.searchBlock}>
                                    <Layout
                                        ref={searchRef}
                                        style={{
                                            background: 'transparent',
                                            maxWidth: '600px',
                                            width: '100%',
                                        }}
                                    >
                                        <Search
                                            enterButton={
                                                <Image
                                                    src={icon_search}
                                                    width={0}
                                                    height={0}
                                                    sizes={'100%'}
                                                    alt={'Найти'}
                                                />
                                            }
                                            className={`${h.searchHead} ${h.headerSearchButton}`}
                                            placeholder={LOCALIZATION.header.searchPlaceholder}
                                            allowClear
                                            size="large"
                                            onSearch={() => {
                                                debounce(handleSearch, 300);
                                            }}
                                            value={searchValue}
                                            onFocus={() => {
                                                setIsShowSearchResult(true);
                                            }}
                                            onBlur={() => {
                                                setTimeout(() => {
                                                    setIsShowSearchResult(false);
                                                }, 100);
                                            }}
                                            onChange={(e) => {
                                                handleSearch_realTime(e.target.value);
                                                setSearchValue(e.target.value);
                                            }}
                                        />
                                    </Layout>
                                    <Flex
                                        ref={resultRef}
                                        className={h.searchResult}
                                        style={{
                                            display: isShowSearchResult ? 'block' : 'none',
                                        }}
                                    >
                                        <Flex
                                            vertical
                                            className={h.searchPiece}
                                        >
                                            {searchResult?.category?.map((el: any, i: any) => {
                                                return (
                                                    <HeaderSearchItem
                                                        key={i}
                                                        onClick={() => {
                                                            setIsShowSearchResult(false);
                                                        }}
                                                        href={el?.url}
                                                        image={el?.logo}
                                                        title={el?.name}
                                                        value={'Категория'}
                                                        type={EHeaderSearchItemType.Category}
                                                        searchPrompt={searchValue}
                                                    />
                                                );
                                            })}
                                        </Flex>
                                        <Flex
                                            vertical
                                            className={h.searchPiece}
                                        >
                                            {searchResult?.organisation?.map((el: any, i: any) => {
                                                return (
                                                    <HeaderSearchItem
                                                        key={i}
                                                        onClick={() => {
                                                            setIsShowSearchResult(false);
                                                        }}
                                                        href={`${el?.url}`}
                                                        image={el?.logo}
                                                        title={el?.name}
                                                        value={'Бренд'}
                                                        type={EHeaderSearchItemType.Brand}
                                                        searchPrompt={searchValue}
                                                    />
                                                );
                                            })}
                                        </Flex>
                                        <Flex
                                            vertical
                                            className={h.searchPiece}
                                        >
                                            {searchResult?.product?.map(
                                                (
                                                    el: {
                                                        name: any;
                                                        url: string | UrlObject;
                                                        logo: any;
                                                        alias: any;
                                                        price: any;
                                                    },
                                                    i: any,
                                                ) => {
                                                    return (
                                                        <HeaderSearchItem
                                                            key={i}
                                                            onClick={() => {
                                                                setIsShowSearchResult(false);
                                                            }}
                                                            href={el?.url}
                                                            image={el?.logo}
                                                            title={el?.name}
                                                            value={
                                                                el?.price ?? LOCALIZATION.header.POA
                                                            }
                                                            type={EHeaderSearchItemType.Product}
                                                            searchPrompt={searchValue}
                                                        />
                                                    );
                                                },
                                            )}
                                        </Flex>
                                    </Flex>
                                </Flex>
                            </Flex>
                            <Flex
                                className={`${h.header_top__right}`}
                                align={'center'}
                                gap={24}
                            >
                                {/*<Button*/}
                                {/*    className={`${h.header_button}`}*/}
                                {/*    type="text"*/}
                                {/*    icon={<PlaceIcon />}*/}
                                {/*    onClick={() => {*/}
                                {/*        setIsShowCatalogHeaderMenu(false)*/}
                                {/*        // dispatch({type: 'TOGGLE_LOCATION_FORM', payload: true})*/}
                                {/*        openLocationForm()*/}
                                {/*    }}*/}
                                {/*>*/}
                                {/*    Не указано*/}
                                {/*</Button>*/}

                                <Dropdown
                                    menu={{
                                        items: contextMenuLanguage,
                                    }}
                                    trigger={['click']}
                                    className={ib.CustomTransparentButton}
                                >
                                    <CustomTransparentButton
                                        onClick={(e) => e.preventDefault()}
                                        IconComponent={() => {
                                            switch (LOCALIZATION.locale as ELocalization) {
                                                case ELocalization.Ru:
                                                    return <IconFlagRU />;
                                                case ELocalization.En:
                                                    return <IconFlagEN />;
                                                case ELocalization.Cn:
                                                    return <IconFlagCN />;
                                                default:
                                                    return <IconFlagRU />;
                                            }
                                        }}
                                        style={{
                                            width: 'fit-content',
                                            aspectRatio: 'unset',
                                            padding: '8px',
                                        }}
                                        childrenStyle={{
                                            borderRadius: '50%',
                                            height: '24px',
                                        }}
                                        title={NLocalization.shortname(LOCALIZATION.locale)}
                                    />
                                </Dropdown>

                                {!isAuthorized ? (
                                    <CustomTransparentButton
                                        IconComponent={LoginIcon}
                                        onClick={() => {
                                            openLoginForm();
                                            setIsShowCatalogHeaderMenu(false);
                                        }}
                                    />
                                ) : (
                                    <Dropdown
                                        menu={{
                                            items: contextMenuUser,
                                        }}
                                        trigger={['click']}
                                        className={ib.CustomTransparentButton}
                                        dropdownRender={(menu: any) => (
                                            <div className={h.header_button_dropdownBlock}>
                                                <Flex
                                                    vertical
                                                    className={h.header_button_dropdownExtra}
                                                >
                                                    <span
                                                        style={{
                                                            fontWeight: '600',
                                                            fontSize: '16px',
                                                            color: '#FFFFFF',
                                                        }}
                                                    >
                                                        {credentialsDto?.fio}
                                                    </span>
                                                    <span
                                                        className={`text-16-400`}
                                                        style={{ color: '#ACAEB0' }}
                                                    >
                                                        {credentialsDto?.email}
                                                    </span>
                                                    {(USERROLE as EProfileType) ===
                                                    EProfileType.Admin ? (
                                                        <Link
                                                            className={`text-16-400 text-[#8D24F8]`}
                                                            href={''}
                                                        >
                                                            {NProfileType.text(
                                                                USERROLE as EProfileType,
                                                            )}
                                                        </Link>
                                                    ) : (
                                                        <span
                                                            className={`text-16-400 text-[#ACAEB0]`}
                                                        >
                                                            {NProfileType.text(
                                                                USERROLE as EProfileType,
                                                            )}
                                                        </span>
                                                    )}
                                                </Flex>
                                                <Divider style={{ margin: '4px 0' }} />
                                                {React.cloneElement(menu)}
                                            </div>
                                        )}
                                    >
                                        <CustomTransparentButton
                                            onClick={(e) => e.preventDefault()}
                                            IconComponent={UserIcon}
                                        />
                                    </Dropdown>
                                )}
                                <CustomTransparentLink
                                    href={'/help'}
                                    IconComponent={HelpIcon}
                                />
                                <CustomTransparentLink
                                    href={'/contacts'}
                                    IconComponent={PhoneIcon}
                                />
                            </Flex>
                        </Flex>
                        <Flex
                            align={'center'}
                            className={h.header_bottom}
                        >
                            <Flex
                                align={'center'}
                                className={h.header_bottom_left}
                                gap={24}
                            >
                                {/*<Link*/}
                                {/*    href="/manufacturers"*/}
                                {/*    onClick={() => {*/}
                                {/*        setIsShowCatalogHeaderMenu(false)*/}
                                {/*    }}*/}
                                {/*    passHref*/}
                                {/*>*/}

                                {/*    /!*<Button*!/*/}
                                {/*    /!*    type="text"*!/*/}
                                {/*    /!*    className={`${h.header_button}`}*!/*/}
                                {/*    /!*>*!/*/}
                                {/*    /!*    Производители*!/*/}
                                {/*    /!*</Button>*!/*/}
                                {/*</Link>*/}
                                <CustomTransparentLink
                                    href={'/manufacturers'}
                                    onClick={() => {
                                        setIsShowCatalogHeaderMenu(false);
                                    }}
                                    IconComponent={FactoryIcon}
                                    noStyle
                                >
                                    {LOCALIZATION.header.manufacturersTitle}
                                </CustomTransparentLink>
                                <Link
                                    href="/requests/production"
                                    onClick={() => {
                                        setIsShowCatalogHeaderMenu(false);
                                    }}
                                    passHref
                                >
                                    <Button
                                        type="text"
                                        className={`${h.header_button}`}
                                    >
                                        {LOCALIZATION.header.queryProduceTitle}
                                    </Button>
                                </Link>
                                <Link
                                    href="/requests/repair"
                                    onClick={() => {
                                        setIsShowCatalogHeaderMenu(false);
                                    }}
                                >
                                    <Button
                                        type="text"
                                        className={`${h.header_button}`}
                                    >
                                        {LOCALIZATION.header.queryRepairTitle}
                                    </Button>
                                </Link>
                            </Flex>
                            <Flex className={h.header_bottom_right}>
                                {!isAuthorized && (
                                    <Button
                                        onClick={() => {
                                            // dispatch({type: 'TOGGLE_REGISTER_FORM', payload: true})
                                            openRegisterForm();
                                        }}
                                        className={h.header_button_primary}
                                    >
                                        {LOCALIZATION.header.registerButton}
                                    </Button>
                                )}
                            </Flex>
                        </Flex>
                    </ConfigProvider>
                </Flex>
                <Layout
                    style={{ display: !isShowCatalogHeaderMenu ? 'none' : 'block' }}
                    className={h.catalogWindow}
                >
                    <CatalogHeaderMenu
                        isShowCatalogHeaderMenu={isShowCatalogHeaderMenu}
                        setIsShowCatalogHeaderMenu={setIsShowCatalogHeaderMenu}
                    />
                </Layout>
            </Flex>
        </Flex>
    );
});

export default Header;
